import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import transformContentHtml from "@insite/client-framework/Common/Utilities/transformContentHtml";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import LazyImage from "@insite/mobius/LazyImage";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import parse from "html-react-parser";
import React, { FC } from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    img = "img",
    imgtext = "imgtext",
    imgDiscribtion = "imgDiscribtion",
    lowerText = "lowerText",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.img]: string;
        [fields.imgtext]: string;
        [fields.imgDiscribtion]: string;
        [fields.lowerText]: string;
    };
}

export interface CustomWidgetStyle {
    secondaryImgWrapper: InjectableCss;
    columnWrapper: InjectableCss;
    descColumnEqualWidthCol: InjectableCss;
    imgColumnEqualWidthCol: InjectableCss;
    imgCaption: InjectableCss;
    lowerText: InjectableCss;
    mobileview: InjectableCss;
    headerstyle: InjectableCss;
}

export const customWidgetStyle: CustomWidgetStyle = {
    headerstyle: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: ${TYPOGRAPHY.size.large};
                    `,
                    css`
                        font-size: ${TYPOGRAPHY.size.large};
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    secondaryImgWrapper: {
        css: css`
            margin-bottom: 56px !important;
        `,
    },
    columnWrapper: {
        css: css`
            display: flex;
            gap: 24px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: flex;
                        flex-direction: column;
                    `,
                    css`
                        display: flex;
                        flex-direction: column;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    descColumnEqualWidthCol: {
        css: css`
            flex: 1;
            height: 100%;
            font-size: ${TYPOGRAPHY.size.large};
            font-weight: ${TYPOGRAPHY.weight.base} !important;
            color: ${getProp("theme.typography.p.color")};
            line-height: 28px !important;
        `,
    },
    imgColumnEqualWidthCol: {
        css: css`
            flex: 1;
            height: 100%;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    imgCaption: {
        css: css`
            font-size: ${TYPOGRAPHY.size.smaller} !important;
            line-height: 20px;
            margin-bottom: 39px !important;
            color: ${getProp("theme.typography.p.color")};
            p {
                font-size: ${TYPOGRAPHY.size.smaller} !important;
                line-height: 20px !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-bottom: 56px;
                    `,
                    css`
                        margin-bottom: 76px;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    lowerText: {
        css: css`
            font-size: ${TYPOGRAPHY.size.large};
            font-weight: ${TYPOGRAPHY.weight.base} !important;
            color: ${getProp("theme.typography.p.color")};
            line-height: 28px !important;
            margin-bottom: 104px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin-top: 24px !important;
                        margin-bottom: 30px !important;
                    `,
                    css`
                        margin-top: 24px !important;
                        margin-bottom: 30px !important;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    mobileview: {
        css: css`
            display: none;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex: 1;
                        height: 100%;
                        display: block;
                        margin-top: 30px;
                    `,
                    css`
                        flex: 1;
                        height: 100%;
                        display: block;
                        margin-top: 30px;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
};

const styles = customWidgetStyle;
export const CENArticleAndBanner: FC<OwnProps> = ({ fields }) => {
    return (
        <>
            <StyledWrapper {...styles.secondaryImgWrapper}>
                <Typography {...styles.headerstyle} variant="h4">
                    {fields.title}
                </Typography>
                <StyledWrapper {...styles.columnWrapper}>
                    <StyledWrapper {...styles.descColumnEqualWidthCol}>{fields.imgtext}</StyledWrapper>
                    <StyledWrapper {...styles.imgColumnEqualWidthCol}>
                        <LazyImage {...styles.imgColumnEqualWidthCol} altText={fields.title} src={fields.img}></LazyImage>
                        <StyledWrapper {...styles.imgCaption}>
                            {parse(transformContentHtml(fields.imgDiscribtion), parserOptions)}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.lowerText}>{fields.lowerText}</StyledWrapper>

                <StyledWrapper {...styles.mobileview}>
                    <LazyImage {...styles.mobileview} altText={fields.title} src={fields.img}></LazyImage>
                    <StyledWrapper {...styles.imgCaption}>
                        {parse(transformContentHtml(fields.imgDiscribtion), parserOptions)}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: CENArticleAndBanner,
    definition: {
        group: "Basic",
        displayName: "CENArticleAndBanner",
        fieldDefinitions: [
            {
                name: fields.title,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                name: fields.img,
                fieldType: "General",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
            },
            {
                name: fields.imgtext,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                name: fields.imgDiscribtion,
                fieldType: "General",
                editorTemplate: "RichTextField",
                defaultValue: "",
            },
            {
                name: fields.lowerText,
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
            },
        ],
    },
};

export default widgetModule;
